import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import { countrySchema } from '@/schemas'
import type {
  BrandInterface,
  CountryInterface,
  FormInputControlType,
  FormInputRegisterType,
  SelectFormControlType,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'
import zodErrorModalMessage from '@/utils/forms/zodErrorModalMessage'

import AtomSelect from '../../Atoms/AtomSelect/AtomSelect'
import HoverElement from '../../Atoms/HoverElement/HoverElement'
import {
  brandCreationSchema,
  type BrandCreationType,
} from './BrandCreationModal.config'
import cn from './BrandCreationModal.module.scss'

type BrandAdditionModalPropsType = {
  open: boolean
  setOpen: (open: boolean) => void
  setBrands?: (brands: BrandInterface[]) => void
  onSuccess?: () => void
}

function BrandCreationModal(props: BrandAdditionModalPropsType) {
  const { open, setBrands, setOpen, onSuccess } = props

  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [countries, setCountries] = useState<CountryInterface[]>([])

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<BrandCreationType>({
    shouldFocusError: false,
    resolver: zodResolver(brandCreationSchema),
  })

  const [modal, contextHolder] = Modal.useModal()

  useEffect(() => {
    if (!open) return

    api
      .get('/countries/all')
      .then((response) => {
        const data = response.data.countries || response.data
        const validatedCountries = countrySchema.array().parse(data)
        setCountries(validatedCountries)
      })
      .catch((error) => {
        if (error instanceof ZodError) {
          zodErrorModalMessage(modal)
        }
        handleApiError(error, modal)
        setOpen(false)
      })
  }, [modal, open, setOpen])

  const handleBrandCreation = async (formData: BrandCreationType) => {
    setConfirmLoading(true)
    api
      .post(`/brands`, {
        description: formData.description,
        countryId: formData.countryOfOrigin.value,
      })
      .then(() => {
        setBrands?.([])
        setOpen(false)
        toast.success('Бренд создан успешно')
        if (onSuccess) onSuccess()
      })
      .catch((error) => {
        handleApiError(error, modal)
      })
      .finally(() => {
        setConfirmLoading(false)
      })
  }

  return (
    <>
      {contextHolder}
      <AtomModalWithForm
        title="Новый бренд"
        open={open}
        submitting={confirmLoading}
        onSubmit={(event) => {
          event.preventDefault()
          void handleSubmit(
            handleBrandCreation,
            handleFormSubmitError(isValid)
          )()
        }}
        onCancel={() => {
          setOpen(false)
          reset()
        }}
      >
        <AtomFormInput
          layout="stacked"
          idPrefix="brand-"
          location="brandCreation"
          name="description"
          title="Наименование"
          placeholder="Например, Rehau"
          errors={errors}
          register={register as FormInputRegisterType}
          control={control as unknown as FormInputControlType}
          required
        />
        <HoverElement>
          <AtomSelect
            layout="stacked"
            fullWidth
            title="Страна"
            placeholder="Выберите страну"
            options={countries.map((country) => ({
              value: country.id,
              label: country.description,
            }))}
            required
            formName="countryOfOrigin"
            formErrors={errors}
            showSearch
            formControl={control as SelectFormControlType}
            openByHover={false}
            popupClassName={cn.selectPopup}
          />
        </HoverElement>
      </AtomModalWithForm>
    </>
  )
}

export default BrandCreationModal
